import { Box, Flex, Grid, Text } from '@chakra-ui/react';
import { FieldLabel } from 'src/components/Field';
import {
  FormFeedback,
  Input,
  TagInput,
  Modal,
  Button
} from '@morressier/alembic';
import { Editor } from '@tinymce/tinymce-react';
import { useFormContext } from 'react-hook-form';
import { FormFieldMultipleFileUpload } from '../FormField';
import { useSubmissionData } from '../../contexts/SubmissionDataProvider';
import { getFileUploadArrayFFDConfigs } from '../../utils/formHelpers';
import { get } from 'lodash';
import PreviewEmail from '../../components/EmailModule/PreviewEmail';
import TestEmailSent from '../../pages/Organization/Workspace/Settings/Emails/TestEmailSent';
import { useState } from 'react';
import { ISubmissionFile } from '@publishing-engine/domain/models';
import { normalizeRecipients } from '../../utils/normalizeEmailRecipients';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretUp } from '@fortawesome/pro-solid-svg-icons';
import { getEmailTemplatePreview } from '../../api/email-service';
import { useMutation } from '@tanstack/react-query';
import { IEmailContentType } from '@publishing-engine/domain/models/email-templates';

export type IEmailFormType = {
  body: string;
  subject: string;
  recipients: {
    to: { text: string }[];
    cc?: { text: string }[];
    bcc?: { text: string }[];
  };
  attachments?: ISubmissionFile[];
};

interface EmailContentForm {
  emailContent: IEmailFormType;
}

interface IEditEmailTemplateProps {
  isCustomTemplate?: boolean;
  showTestEmail?: boolean;
  eventName?: string;
  revisionSeverity?: string;
  formPayload?: Record<string, unknown>;
  reviewDetails?: {
    reviewId: string;
    reviewRequestId?: string;
  };
}

// TagInput renders tags with `whiteSpace: nowrap`, causing long email
// addresses to overflow. Allow them to wrap within the field instead.
const recipientTagInputSx = {
  width: '100%',
  maxWidth: '100%',
  overflow: 'hidden',
  '& span': {
    whiteSpace: 'normal !important',
    wordBreak: 'break-word'
  }
};

const EditEmailTemplate = ({
  showTestEmail = false,
  eventName,
  revisionSeverity,
  formPayload,
  reviewDetails
}: IEditEmailTemplateProps) => {
  const {
    control,
    setValue,
    register,
    watch,
    trigger,
    formState: { errors }
  } = useFormContext<EmailContentForm>();
  const [openPreviewEmail, setOpenPreviewEmail] = useState(false);
  const apiKeyTinymce = import.meta.env.VITE_TINY_MCE_CLIENT_KEY;
  const selfHostedTinymce = import.meta.env.VITE_SELF_HOSTED_TINY_MCE;

  const { submissionId, submissionContextId, organizationId } =
    useSubmissionData();
  const bodyError = get(errors, 'emailContent.body');
  const emailContent = watch('emailContent');

  const [showBcc, setShowBcc] = useState(false);
  const [previewTemplate, setPreviewTemplate] =
    useState<IEmailContentType | null>(null);

  const previewMutation = useMutation({
    mutationFn: getEmailTemplatePreview,
    onSuccess: data => {
      setPreviewTemplate(data.renderedEmail);
      setOpenPreviewEmail(true);
    },
    onError: () => {
      throw new Error(`Something went wrong for preview`);
    }
  });

  const fetchPreview = async () => {
    const event = {
      organizationId,
      submissionContextId,
      submissionId,
      type: eventName,
      subType: revisionSeverity,
      formPayload,
      ...(eventName === 'ReviewRated' ? reviewDetails : {}),
    };

    const template = {
      ...emailContent,
      recipients: normalizeRecipients(emailContent.recipients)
    };

    await previewMutation.mutateAsync({ template, event });
  };

  return (
    <Flex gap={4} flexDirection="column" padding={4}>
      <Flex
        borderRadius="small"
        backgroundColor="surfacePrimaryFill"
        flexDirection="column"
        gap={6}
      >
        <Grid
          templateColumns="150px 1fr"
          rowGap={3}
          columnGap={2}
          alignItems="flex-start"
        >
          <FieldLabel label="To" isRequired subText="" />
          <Flex flexDirection="column" width="100%">
            <TagInput
              name="emailContent.recipients.to"
              control={control}
              disabled
              isInvalid={Boolean(errors?.emailContent?.recipients?.to)}
              onBlur={() => trigger('emailContent.recipients.to')}
              sx={recipientTagInputSx}
            />
            {errors.emailContent?.recipients?.to && (
              <FormFeedback variant="error">
                {errors.emailContent.recipients.to.message}
              </FormFeedback>
            )}
          </Flex>

          <FieldLabel label="Cc" subText="" isRequired={false} />
          <Flex width="100%" alignItems="center">
            <Flex flex="1" flexDirection="column">
              <TagInput
                name="emailContent.recipients.cc"
                control={control}
                placeholder="Enter optional recipient email address(es)"
                sx={recipientTagInputSx}
              />
              {errors.emailContent?.recipients?.cc && (
                <FormFeedback variant="error">
                  {errors.emailContent.recipients.cc.message}
                </FormFeedback>
              )}
            </Flex>

            <Button
              ml={2}
              variant="secondary"
              onClick={() => setShowBcc(!showBcc)}
              iconRight={
                showBcc ? (
                  <FontAwesomeIcon icon={faCaretUp} />
                ) : (
                  <FontAwesomeIcon icon={faCaretDown} />
                )
              }
            >
              Bcc
            </Button>
          </Flex>

          {showBcc && (
            <>
              <FieldLabel label="Bcc" subText="" isRequired={false} />
              <Flex flexDirection="column" width="100%">
                <TagInput
                  name="emailContent.recipients.bcc"
                  control={control}
                  placeholder="Enter hidden recipient email address(es)"
                  sx={recipientTagInputSx}
                />
                {errors.emailContent?.recipients?.bcc && (
                  <FormFeedback variant="error">
                    {errors.emailContent.recipients.bcc.message}
                  </FormFeedback>
                )}
              </Flex>
            </>
          )}

          <FieldLabel label="Subject" subText="" isRequired />
          <Flex flexDirection="column" width="100%">
            <Input
              {...register('emailContent.subject')}
              isInvalid={Boolean(errors.emailContent?.subject)}
              placeholder="Enter email subject"
            />
            {errors.emailContent?.subject && (
              <FormFeedback variant="error">
                {errors.emailContent.subject.message}
              </FormFeedback>
            )}
          </Flex>
        </Grid>

        <Flex flexDirection="column" gap={2}>
          <Flex justifyContent="space-between" alignItems="center">
            <FieldLabel label="Body Text" isRequired subText={undefined} />
            <Button
              variant="secondary"
              onClick={() => fetchPreview()}
              disabled={previewMutation.isPending}
              isLoading={previewMutation.isPending}
            >
              Preview
            </Button>
          </Flex>
          <Box width="100%">
            <Box
              className={bodyError ? 'editorErrorContainer' : ''}
              width="100%"
            >
              <Editor
                {...(apiKeyTinymce
                  ? { apiKey: apiKeyTinymce }
                  : { tinymceScriptSrc: selfHostedTinymce })}
                licenseKey={selfHostedTinymce ? 'gpl' : ''}
                value={watch('emailContent.body')}
                onEditorChange={(content: string) =>
                  setValue('emailContent.body', content, {
                    shouldValidate: true,
                    shouldDirty: true
                  })
                }
                init={{
                  menubar: false,
                  statusbar: false,
                  paste_as_text: true,
                  plugins: ['math', 'autoresize'],
                  toolbar:
                    'undo redo | formatselect | bold italic underline strikethrough | ' +
                    'alignleft aligncenter alignright alignjustify | ' +
                    'bullist numlist outdent indent | ' +
                    'link image table hr charmap | code preview | math',
                  min_height: 400,
                  entity_encoding: 'raw'
                }}
              />
            </Box>
            {bodyError && (
              <FormFeedback variant="error">
                {bodyError.message as string}
              </FormFeedback>
            )}
          </Box>
        </Flex>
      </Flex>
      <Flex flexDirection="column" gap={2}>
        <FormFieldMultipleFileUpload
          submissionContextId={submissionContextId}
          submissionId={submissionId}
          config={getFileUploadArrayFFDConfigs('emailContent.attachments')}
          _usePublicUploads
        />
      </Flex>

      {showTestEmail && (
        <TestEmailSent
          template={emailContent}
          organizationId={organizationId}
        />
      )}
      {openPreviewEmail && (
        <Modal isOpen onClose={() => setOpenPreviewEmail(false)} width="720px">
          <Flex paddingX={4} paddingTop={4} paddingBottom={0}>
            <Text textStyle="headingGroup">Preview Email</Text>
          </Flex>
          <Box margin={4}>
            <PreviewEmail template={previewTemplate} />
          </Box>
        </Modal>
      )}
    </Flex>
  );
};

export default EditEmailTemplate;
